import React, { useState, useCallback } from 'react';
import ReactDOM from 'react-dom';

import { CommonModal, CommonModalProps } from '../CommonModal';

export interface WrapModalProps<TResolved extends any = any, TRejected extends any = any> {
  hide(): void;
  onResolve(arg?: TResolved): void;
  onReject(arg?: TRejected): void;
}

export function wrapModalPromise<T extends object | undefined, R = unknown>(
  Component: (arg: T & WrapModalProps<R>) => JSX.Element | null,
  modalProps?: CommonModalProps,
) {
  return (props: T): Promise<R> => {
    const div = document.createElement('div');
    document.body.appendChild(div);

    const WrappedComponent = ({
      onResolve,
      onReject,
    }: Omit<WrapModalProps, 'hide'>): React.ReactElement<typeof CommonModal> => {
      const [open, setOpen] = useState(true);

      const hide = useCallback(() => {
        setOpen(false);
        // in case long time no resolve promise cause memory leak
        // if you want resolve this promise, just call [[onResolve]] before hide
        // the promise state won't change after resolved
        onReject('hide');
      }, [setOpen, onReject]);

      return (
        <CommonModal on={open} turnOff={hide} {...modalProps}>
          <Component {...props} hide={hide} onReject={onReject} onResolve={onResolve} />
        </CommonModal>
      );
    };

    return new Promise((resolve, reject) => {
      const handleCancel = (arg: any) => {
        ReactDOM.unmountComponentAtNode(div);
        div.parentElement?.removeChild(div);
        reject(arg);
      };

      ReactDOM.render(<WrappedComponent onResolve={resolve} onReject={handleCancel} />, div);
    });
  };
}
